import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useReturnRequestsTimeChart from "@babackapp/private-api-client/client/use-return-requests-time-chart";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import { useMemo } from "react";
import { Label, Pie, PieChart } from "recharts";
import { useDashboardFilters } from "../lib/dashboard-filters";
import useFormattedTimeRange from "../lib/use-formatted-time-range";
import {
  discountColor,
  exchangeColor,
  exchangeCreditColor,
  refundColor,
  unknownColor,
} from "./colors";

function usePieChartConfig() {
  const t = useTranslation();
  return {
    returns: {
      label: t("analyticsDashboard.resolutionRepartition.title"),
    },
    discount: {
      label: t("analyticsDashboard.resolutionTypes.discount"),
      color: discountColor,
    },
    exchange: {
      label: t("analyticsDashboard.resolutionTypes.exchange"),
      color: exchangeColor,
    },
    exchangeCredit: {
      label: t("analyticsDashboard.resolutionTypes.exchangeCredit"),
      color: exchangeCreditColor,
    },
    refund: {
      label: t("analyticsDashboard.resolutionTypes.refund"),
      color: refundColor,
    },
    unknown: {
      label: t("analyticsDashboard.resolutionTypes.other"),
      color: unknownColor,
    },
  } satisfies ChartConfig;
}

type ReturnRequestResolutionPieChartProps = {
  setResolutionFilter: (
      resolution: string | undefined,
  ) => void;
};

export function ReturnRequestResolutionPieChart(
    { setResolutionFilter }: ReturnRequestResolutionPieChartProps
) {
  const { data: shop } = useCurrentShopSuspense();
  const { toTimestamp, fromTimestamp, productId, variantId, resolution, reason } =
    useDashboardFilters();

  const t = useTranslation();
  const pieChartConfig = usePieChartConfig();

  const { data: timeChartData } = useReturnRequestsTimeChart(shop.id, {
    fromTimestamp: fromTimestamp ?? 0,
    toTimestamp: toTimestamp ?? 0,
    productId,
    variantId,
    resolution,
    reason
  });

  const formattedTimeRange = useFormattedTimeRange();

  const data = useMemo(
    () =>
      (timeChartData ?? []).reduce(
        (acc, entry) => {
          acc.discount += entry.discount.count;
          acc.exchange += entry.exchange.count;
          acc.refund += entry.refund.count;
          acc.unknown += entry.unknown.count;
          acc.exchangeCredit += entry.exchangeCredit.count;
          acc.total +=
            entry.discount.count +
            entry.exchange.count +
            entry.refund.count +
            entry.unknown.count +
            entry.exchangeCredit.count;
          return acc;
        },
        {
          discount: 0,
          exchange: 0,
          refund: 0,
          unknown: 0,
          exchangeCredit: 0,
          total: 0,
        },
      ),
    [timeChartData],
  );

  const chartData = [
    {
      name: t("analyticsDashboard.resolutionTypes.discount"),
      type: "discount",
      returns: data.discount,
      fill: discountColor,
    },
    {
      name: t("analyticsDashboard.resolutionTypes.exchange"),
      type: "exchange",
      returns: data.exchange,
      fill: exchangeColor,
    },
    {
      name: t("analyticsDashboard.resolutionTypes.exchangeCredit"),
      type: "exchange-credit",
      returns: data.exchangeCredit,
      fill: exchangeCreditColor,
    },
    {
      name: t("analyticsDashboard.resolutionTypes.refund"),
      type: "refund",
      returns: data.refund,
      fill: refundColor,
    },
    {
      name: t("analyticsDashboard.resolutionTypes.other"),
      type: "",
      returns: data.unknown,
      fill: unknownColor,
    },
  ];

  return (
    <Card className="col-span-1 flex flex-col w-full">
      <CardHeader className="pb-0">
        <CardTitle className="text-md">
          {t("analyticsDashboard.resolutionRepartition.title")}
        </CardTitle>
        <CardDescription>{formattedTimeRange}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-2 flex justify-center items-center">
        <ChartContainer
          config={pieChartConfig}
          className="h-[200px] w-[200px] max-w-full"
        >
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Pie
              data={chartData}
              dataKey="returns"
              nameKey="name"
              innerRadius="70%"
              outerRadius="100%"
              strokeWidth={5}
              onClick={(data) => setResolutionFilter(data.type)}
              cursor="pointer"
            >
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-3xl font-bold"
                        >
                          {data.total}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          {t("analyticsDashboard.resolutionRepartition.label")}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
          </PieChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="flex text-center items-center gap-2 leading-snug font-medium">
          {t("analyticsDashboard.resolutionRepartition.subtitle")}
        </div>
      </CardFooter>
    </Card>
  );
}
